import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ModalAddUbicaciones } from "./components/ModalAddUbicaciones";
import { ModalEditarUbicacion } from "./components/ModalEditarUbicacion";
import "./GestionUbicaciones.css";
import { sendAjaxRequest } from "@/ts/api/comunicacion";
import { HorizontalTabs, VerticalTabs } from "@/components/Tabs";
import { Breadcrumb } from "@/components/Breadcrumb";
import { useDisclosure, useDisclosureData } from "@/hooks/useModal";
import { QueryError, Loading } from "@/components/Loading/Loading";
import { formatIDArt } from "@/ts/util";
import { useOnChange } from "@/hooks/useOnChange";

export function GestionUbicacionesPage() {
	const discloseNewUbi = useDisclosure();
	const { data, error, isPending } = useEstanterias();

	return (
		<div id="content" className="content" >
			<title>Piher | Gestión de Ubicaciones</title>

			<div>
				<Breadcrumb
					sectionTitle="Gestión de Ubicaciones"
					links={[
						{ label: "Gestión de Ubicaciones", active: true }
					]}
				/>


				<div style={{ display: "flex", justifyContent: "space-between" }}>
					<div className="divLeyenda">
						<svg width={20} height={20}>
							<rect stroke="black" strokeWidth="1px" width={20} height={20} fill="#f2d2ac" />
						</svg>
						<h4>Tiene articulo</h4>
						<img src="/img/icons/articuloFijo.svg" width={16} />
						<h4> Es articulo fijo</h4>
					</div>
					<button onClick={discloseNewUbi.open} className="btnPrimary" type="button">Nueva ubicación</button>
				</div>

				{isPending == true && <Loading />}
				{error != null && <QueryError error={error} fallback="Error cargando las estanterías" />}
				{data != null && (
					<TabbableZonas discloseNewUbi={discloseNewUbi} estanterias={data} />
				)}
			</div>

		</div>
	);
}

function TabbableZonas(props: { readonly discloseNewUbi: ReturnType<typeof useDisclosure>; readonly estanterias: BasicEstanteriaInfo[]; }) {
	const [currentZona, setCurrentZona] = useState("A");
	const [currentFila, setCurrentFila] = useState("01");

	useOnChange(currentZona, () => {
		setCurrentFila("01");
	});

	const { data, error, isPending } = useEstanteria(currentZona, currentFila);

	const tabsEstanterias: Array<[string, string]> = props.estanterias
		.filter(shelf => shelf.idZona === currentZona)
		.map(shelf => shelf.idEstanteria)
		.map(fila => [fila, fila]);

	return (
		<div className="tabbable">
			<HorizontalTabs
				options={keysAlmacen.map(alm => [alm, alm])}	// Los valores son iguales a las etiquetas
				value={currentZona}
				setValue={setCurrentZona}
			/>
			<div className="contenedorAlmacen">
				<VerticalTabs
					options={tabsEstanterias}
					value={currentFila}
					setValue={setCurrentFila}
				/>

				<div style={{ padding: "20px", flex: 1, border: "1px solid #ccc", overflowY: "auto" }}>
					<h1 style={{ textAlign: "center", marginTop: 0 }}>{currentZona}{currentFila}</h1>

					{isPending == true && <Loading />}
					{error != null && <QueryError error={error} fallback="Error cargando las ubicaciones" />}

					{data != null && (
						<TablaEstanteria ubicaciones={data} zona={currentZona} fila={currentFila} />
					)}
				</div>
			</div>

			<ModalAddUbicaciones show={props.discloseNewUbi.isOpen} close={props.discloseNewUbi.close} currentZona={currentZona} currentEstanteria={currentFila} />
		</div>
	);
}




function TablaEstanteria(props: { readonly ubicaciones: BasicCeldaInfo[]; readonly zona: string; readonly fila: string; }) {
	const discloseUbicacion = useDisclosureData(null as string | null);

	const columnasExistentes = props.ubicaciones
		.map(ubicacion => keysColumn.indexOf(ubicacion.idColumna))
		.filter(index => index !== -1);

	const alturasExistentes = props.ubicaciones
		.map(ubicacion => Number(ubicacion.idAltura))
		.filter(Number.isFinite);

	const posicionesOcupadas = new Set(
		props.ubicaciones.map(ubicacion =>
			`${keysColumn.indexOf(ubicacion.idColumna)}-${Number(ubicacion.idAltura)}`
		)
	);

	const celdasVacias: Array<{ readonly id: string; readonly columna: number; readonly altura: number }> = [];

	if (columnasExistentes.length > 0 && alturasExistentes.length > 0) {
		const minColumna = Math.min(...columnasExistentes);
		const maxColumna = Math.max(...columnasExistentes);
		const minAltura = Math.min(...alturasExistentes);
		const maxAltura = Math.max(...alturasExistentes);

		for (let columna = minColumna; columna <= maxColumna; columna++) {
			for (let altura = minAltura; altura <= maxAltura; altura++) {
				if (posicionesOcupadas.has(`${columna}-${altura}`)) continue;

				celdasVacias.push({
					id: `vacio-${columna}-${altura}`,
					columna,
					altura,
				});
			}
		}
	}

	return (
		<div className="tablaEstanteria">
			{celdasVacias.map(celda => (
				<div
					key={celda.id}
					className="celdaUbicacion fondoGone"
					style={{
						gridColumnStart: String(celda.columna + 1),
						gridColumnEnd: String(celda.columna + 1),
						gridRowStart: String(celda.altura * -1),
						gridRowEnd: String(celda.altura * -1),
					}}
				/>
			))}

			{props.ubicaciones.map(ubicacion => (
				<CeldaEstanteria key={ubicacion.idUbicacion} ubicacion={ubicacion} onClick={() => { discloseUbicacion.open(ubicacion.idUbicacion); }} />
			))}

			<ModalEditarUbicacion
				idUbicacion={discloseUbicacion.data}
				zona={props.zona} fila={props.fila}
				show={discloseUbicacion.isOpen}
				close={discloseUbicacion.close}
			/>
		</div>
	);
}
function CeldaEstanteria(props: { readonly ubicacion: BasicCeldaInfo, readonly onClick: () => void; }) {
	const claseFondo = props.ubicacion.articulos.length == 0 ? "" : "fondoAmarillo";

	const idColumna = keysColumn.indexOf(props.ubicacion.idColumna) + 1;
	const idAltura = parseInt(props.ubicacion.idAltura, 10) * -1;
	const style = {
		gridColumnStart: String(idColumna),
		gridColumnEnd: String(idColumna),
		gridRowStart: String(idAltura),
		gridRowEnd: String(idAltura),
	};

	return (
		<div className={`celdaUbicacion ${claseFondo} hoverDark`} style={style} onClick={props.onClick}>
			<h3>{props.ubicacion.idUbicacion}</h3>
			<ArticulosEstanteria articulos={props.ubicacion.articulos} maxArticulosVisibles={3} />
		</div>
	);
}
function ArticulosEstanteria(props: { readonly articulos: ArticuloEnCelda[]; readonly maxArticulosVisibles: number; }) {
	const shownArts = useMemo(() => {
		const artsAgregados = new Map<string, { id: string; cantidad: number, esFijo: boolean, }>();
		for (const articulo of props.articulos) {
			const key = formatIDArt(articulo.idArticulo, articulo.idFase);
			const artExistente = artsAgregados.get(key);
			if (artExistente == null) {
				artsAgregados.set(key, { id: key, cantidad: articulo.cantidad, esFijo: articulo.esFijo });
			} else {
				artExistente.cantidad += articulo.cantidad;
			}
		}

		const filtrados = [];

		let indiceArticulo = 0;
		for (const art of artsAgregados.values()) {
			if (indiceArticulo == props.maxArticulosVisibles) {
				filtrados[2] = (
					<div key="ellipsis">+</div>
				);
				break;
			}

			const iconoFijo = art.esFijo ? <img src="/img/icons/articuloFijo.svg" width={16} /> : "";
			const claseRojo = art.cantidad < 0 ? "red bold" : "";
			filtrados.push(
				<div key={art.id} className={`articuloUbiItem ${claseRojo}`}>
					{iconoFijo}
					<span>{art.id}</span>
					{/* No me gusta que pongamos un - como separador, ya que puede haber rotura de stock. */}
					<span className="cantidad"> {art.cantidad}u</span>
				</div>
			);

			indiceArticulo++;
		}

		return filtrados;
	}, [props.articulos, props.maxArticulosVisibles]);

	return (
		<div className="articulosUbi">{shownArts}</div>
	);
}


export const keysAlmacen = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L"];
export const keysColumn = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L"];


export function useEstanterias() {
	return useQuery<BasicEstanteriaInfo[]>({
		queryKey: ["getEstanterias"],
		queryFn: () => {
			return sendAjaxRequest<BasicEstanteriaInfo[]>("GET", "/api/v1/getEstanterias");
		}
	});
}
export function useEstanteria(zona: string, estanteria: string) {
	return useQuery<BasicCeldaInfo[]>({
		queryKey: ["getEstanteria", zona, estanteria],
		queryFn: () => {
			return sendAjaxRequest<BasicCeldaInfo[]>("GET", "/api/v1/getEstanteria", { zona, estanteria });
		}
	});
}
interface BasicEstanteriaInfo {
	idZona: string,
	idEstanteria: string,
}
interface BasicCeldaInfo {
	idUbicacion: string;

	idAlmacen: string;
	idEstanteria: string;
	idColumna: typeof keysColumn[number];
	idAltura: string;

	articulos: ArticuloEnCelda[];
}
interface ArticuloEnCelda {
	cantidad: number;
	esFijo: boolean;
	idArticulo: string;
	idFase: number;
	idContenedor: number | null;
}